import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle, MusicNotes, X, Play, Pause, DownloadSimple, FilmStrip } from "@phosphor-icons/react";
import { creatorHref } from "../lib/links";
import { usePlayer } from "../context/PlayerContext";
import LazyImage from "./LazyImage";

const SEEN_KEY = "aa_inspo_seen_date";

function todayStamp() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Full-screen "drop" moment shown once per day when the site loads — a big,
 * centered takeover in the style of a clothing brand's launch popup, rather
 * than a small persistent corner widget.
 */
export default function DailyInspoPopup({ inspo, onOpenPack }) {
  const [open, setOpen] = useState(false);
  const { play, toggle, current, isPlaying } = usePlayer();
  const [visible, setVisible] = useState(false);
  const playing = !!inspo?.audio && current?.id === inspo.audio.id && isPlaying;

  useEffect(() => {
    if (!inspo || (!inspo.pack && !inspo.audio)) return;
    let seen = "";
    try { seen = localStorage.getItem(SEEN_KEY) || ""; } catch { /* ignore */ }
    if (seen === todayStamp()) return;
    setOpen(true);
    // Small delay so it feels like an intentional reveal, not a jarring flash.
    const t = setTimeout(() => setVisible(true), 150);
    return () => clearTimeout(t);
  }, [inspo]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    document.body.dataset.overlay = "1"; // keeps the bottom player above this popup
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      delete document.body.dataset.overlay;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!inspo || (!inspo.pack && !inspo.audio) || !open) return null;

  const toggleAudio = (e) => {
    e.stopPropagation();
    if (!inspo.audio) return;
    if (current?.id === inspo.audio.id) toggle();
    else play(inspo.audio, [inspo.audio]);
  };

  const close = () => {
    try { localStorage.setItem(SEEN_KEY, todayStamp()); } catch { /* ignore */ }
    setVisible(false);
    setTimeout(() => setOpen(false), 250);
  };

  const heroImage = inspo.pack?.thumbnail_url || inspo.audio?.cover_url || "";
  const kicker = inspo.pack ? inspo.pack.movie_title : inspo.audio?.creator_name || "";
  const headline = inspo.title || inspo.pack?.title || inspo.audio?.title || "Today's pick";

  return (
    <div
      data-testid="daily-inspo-popup"
      onClick={close}
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      <div
        onClick={(e) => e.stopPropagation()}
        className={`transform-gpu relative w-full max-w-4xl rounded-2xl overflow-hidden border border-white/10 bg-[#121212] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)] transition-all duration-300 ${visible ? "scale-100 translate-y-0" : "scale-95 translate-y-4"}`}
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          data-testid="daily-inspo-close"
          className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-black/50 hover:bg-[#880015] backdrop-blur flex items-center justify-center text-white transition"
        >
          <X size={16} weight="bold" />
        </button>

        <div className="grid md:grid-cols-2">
          {/* Hero image side */}
          <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[420px]">
            <LazyImage
              src={heroImage}
              width={960}
              alt={headline}
              className="absolute inset-0 w-full h-full"
              imgClassName="w-full h-full object-cover"
              fallback={<div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#232323] to-[#161616]"><MusicNotes size={48} className="text-white/20" /></div>}
            />
            <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-[#121212] via-transparent to-transparent md:from-[#121212]/90 md:via-[#121212]/10" />
          </div>

          {/* Content side */}
          <div className="relative p-8 md:p-12 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.35em] text-[#FF8090] mb-4">
              <Sparkle size={12} weight="fill" /> Today's drop
            </div>

            {kicker && (
              <div className="text-[11px] font-mono uppercase tracking-widest text-white/40 mb-2">{kicker}</div>
            )}

            <h2 className="font-display font-black text-3xl md:text-4xl leading-[0.95] tracking-tight mb-4">
              {headline}
            </h2>

            {inspo.tagline && (
              <p className="text-white/55 text-sm leading-relaxed mb-8 max-w-sm">{inspo.tagline}</p>
            )}

            <div className="flex flex-wrap items-center gap-3">
              {inspo.pack && (
                <>
                  <button
                    type="button"
                    onClick={() => { onOpenPack?.(inspo.pack.id); close(); }}
                    data-testid="inspo-open-pack"
                    className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF8090] hover:bg-[#FF6070] text-[#121212] font-display font-bold text-xs uppercase tracking-wider transition"
                  >
                    <DownloadSimple size={14} weight="bold" /> Get this scenepack
                  </button>
                  <Link
                    to={`/scenepacks/movie/${inspo.pack.movie_slug}`}
                    onClick={close}
                    data-testid="inspo-open-scenepack"
                    className="flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 hover:border-white/50 hover:bg-white/5 font-display font-bold text-xs uppercase tracking-wider transition"
                  >
                    <FilmStrip size={14} weight="fill" /> View scenepack
                  </Link>
                </>
              )}

              {inspo.audio && !inspo.pack && (
                <button
                  type="button"
                  onClick={toggleAudio}
                  data-testid="inspo-play-audio"
                  className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF8090] hover:bg-[#FF6070] text-[#121212] font-display font-bold text-xs uppercase tracking-wider transition"
                >
                  {playing
                    ? <><Pause size={14} weight="fill" /> Pause preview</>
                    : <><Play size={14} weight="fill" /> Play preview</>}
                </button>
              )}
            </div>

            {inspo.audio && inspo.pack && (
              <div className="flex items-center gap-3 mt-6 pt-6 border-t border-white/10">
                <div className="relative w-11 h-11 rounded-lg overflow-hidden bg-[#232323] ring-1 ring-white/10 flex-shrink-0">
                  <LazyImage
                    src={inspo.audio.cover_url}
                    width={320}
                    alt=""
                    className="absolute inset-0 w-full h-full"
                    imgClassName="w-full h-full object-cover"
                    fallback={<div className="w-full h-full flex items-center justify-center"><MusicNotes size={16} className="text-white/30" /></div>}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-xs truncate">{inspo.audio.title}</div>
                  <Link
                    to={creatorHref({ id: inspo.audio.creator_id, username: inspo.audio.creator_username })}
                    onClick={close}
                    className="text-[10px] text-white/45 hover:text-[#FF8090] truncate transition"
                  >
                    {inspo.audio.creator_name}
                  </Link>
                </div>
                <button
                  type="button"
                  onClick={toggleAudio}
                  data-testid="inspo-play-audio"
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#FF8090] hover:text-[#121212] flex items-center justify-center transition flex-shrink-0"
                  aria-label={playing ? "Pause preview" : "Play preview"}
                >
                  {playing
                    ? <Pause size={13} weight="fill" />
                    : <Play size={13} weight="fill" className="translate-x-0.5" />}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
